import React, { createContext, useState, useContext, ReactNode, useRef } from 'react';

interface ZoomContextType {
  refAreaLeft: string | undefined;
  refAreaRight: string | undefined;
  startIndex: number;
  endIndex: number;
  timeRange: { start?: string; end?: string };
  setRefAreaLeft: (value: string | undefined) => void;
  setRefAreaRight: (value: string | undefined) => void;
  setTimeRange: (range: { start?: string; end?: string }, isUserAction?: boolean) => void;
  setStartIndex: (index: number) => void;
  setEndIndex: (index: number) => void;
  resetZoom: () => void;
  isUserAction: boolean;
  lastUpdated: number; // Timestamp of last update to help track changes
  dataReady: boolean; // Flag to indicate when new data is ready
  setDataReady: (ready: boolean) => void;
  isLoading: boolean; // Flag to indicate when data is being fetched
  setIsLoading: (loading: boolean) => void;
}

export const ZoomContext = createContext<ZoomContextType | undefined>(undefined);

export function ZoomProvider({ children }: { children: ReactNode }) {
  const [refAreaLeft, setRefAreaLeft] = useState<string | undefined>(undefined);
  const [refAreaRight, setRefAreaRight] = useState<string | undefined>(undefined);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [endIndex, setEndIndex] = useState<number>(Infinity);
  const [timeRange, setTimeRangeState] = useState<{ start?: string; end?: string }>({});
  const [isUserAction, setIsUserAction] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());
  const [dataReady, setDataReady] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Make a deep comparison to see if the time range has actually changed
  const prevTimeRangeRef = useRef<{ start?: string; end?: string }>({});
  
  // Enhanced version that tracks source and prevents duplicate updates
  const setTimeRange = (range: { start?: string; end?: string }, userAction: boolean = false) => {
    // Only update if the range has actually changed
    if (prevTimeRangeRef.current.start !== range.start || 
        prevTimeRangeRef.current.end !== range.end) {
      
      // Update the ref to the new value
      prevTimeRangeRef.current = { ...range };
      
      // Set the user action flag
      setIsUserAction(userAction);
      
      // Update the timestamp
      setLastUpdated(Date.now());
      
      // Update the actual state
      setTimeRangeState(range);
    }
  };

  const resetZoom = () => {
    setStartIndex(0);
    setEndIndex(Infinity);
    setTimeRange({}, true); // Mark as user action
  };

  return (
    <ZoomContext.Provider
      value={{
        refAreaLeft,
        refAreaRight,
        startIndex,
        endIndex,
        timeRange,
        setRefAreaLeft,
        setRefAreaRight,
        setTimeRange,
        setStartIndex,
        setEndIndex,
        resetZoom,
        isUserAction,
        lastUpdated,
        dataReady,
        setDataReady,
        isLoading,
        setIsLoading
      }}
    >
      {children}
    </ZoomContext.Provider>
  );
}

export const useZoomContext = () => {
  const context = useContext(ZoomContext);
  if (context === undefined) {
    throw new Error('useZoomContext must be used within a ZoomProvider');
  }
  return context;
};
